import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
  FaTimes,
  FaCoins,
  FaGift,
} from 'react-icons/fa';

/**
 * トーストメッセージのタイプ定義
 */
const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  MINING: 'mining',
  OPPORTUNITY_LOSS: 'opportunity_loss',
};

/**
 * 個別のトーストメッセージコンポーネント
 */
const ToastMessage = ({
  id,
  type,
  title,
  message,
  duration,
  onClose,
  data,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // タイプ別の設定
  const typeConfig = {
    [TOAST_TYPES.SUCCESS]: {
      icon: <FaCheckCircle className="text-green-500" />,
      bgClass: 'bg-green-50 border-green-200',
      titleClass: 'text-green-800',
      messageClass: 'text-green-700',
    },
    [TOAST_TYPES.ERROR]: {
      icon: <FaTimesCircle className="text-red-500" />,
      bgClass: 'bg-red-50 border-red-200',
      titleClass: 'text-red-800',
      messageClass: 'text-red-700',
    },
    [TOAST_TYPES.WARNING]: {
      icon: <FaExclamationTriangle className="text-yellow-500" />,
      bgClass: 'bg-yellow-50 border-yellow-200',
      titleClass: 'text-yellow-800',
      messageClass: 'text-yellow-700',
    },
    [TOAST_TYPES.INFO]: {
      icon: <FaInfoCircle className="text-blue-500" />,
      bgClass: 'bg-blue-50 border-blue-200',
      titleClass: 'text-blue-800',
      messageClass: 'text-blue-700',
    },
    [TOAST_TYPES.MINING]: {
      icon: <FaCoins className="text-yellow-500" />,
      bgClass: 'bg-gradient-to-r from-yellow-50 to-green-50 border-yellow-200',
      titleClass: 'text-yellow-800',
      messageClass: 'text-green-700',
    },
    [TOAST_TYPES.OPPORTUNITY_LOSS]: {
      icon: <FaGift className="text-orange-500" />,
      bgClass: 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200',
      titleClass: 'text-orange-800',
      messageClass: 'text-red-700',
    },
  };

  const config = typeConfig[type] || typeConfig[TOAST_TYPES.INFO];

  useEffect(() => {
    // 表示アニメーション
    setIsVisible(true);

    // 自動消去タイマー
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 300);
  };

  return (
    <div
      className={`
        transform transition-all duration-300 ease-out mb-4 max-w-sm
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div
        className={`
        border rounded-lg shadow-lg p-4 relative
        ${config.bgClass}
      `}
      >
        {/* クローズボタン */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FaTimes size={12} />
        </button>

        {/* メインコンテンツ */}
        <div className="flex items-start space-x-3 pr-6">
          <div className="flex-shrink-0 text-xl">{config.icon}</div>

          <div className="flex-1">
            {title && (
              <h4 className={`font-semibold text-sm mb-1 ${config.titleClass}`}>
                {title}
              </h4>
            )}

            <p className={`text-sm ${config.messageClass}`}>{message}</p>

            {/* マイニング専用の追加情報 */}
            {type === TOAST_TYPES.MINING && data && (
              <div className="mt-2 p-2 bg-white bg-opacity-50 rounded text-xs">
                <div className="flex justify-between items-center">
                  <span>獲得量:</span>
                  <span className="font-bold text-green-600">
                    +{data.amount} {data.tokenType || 'SHINKETSU'}
                  </span>
                </div>
                {data.ecosystemCategory && (
                  <div className="flex justify-between items-center mt-1">
                    <span>生態系:</span>
                    <span className="font-medium text-blue-600">
                      {data.ecosystemCategory}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* 機会損失専用の追加情報 */}
            {type === TOAST_TYPES.OPPORTUNITY_LOSS && data && (
              <div className="mt-2 p-2 bg-white bg-opacity-50 rounded text-xs">
                <div className="flex justify-between items-center">
                  <span>機会損失:</span>
                  <span className="font-bold text-orange-600">
                    {data.amount} {data.tokenType || 'SHINKETSU'}
                  </span>
                </div>
                <div className="mt-1 text-center">
                  <button
                    className="text-blue-600 underline hover:text-blue-800"
                    onClick={() => data.onKycClick?.()}
                  >
                    今すぐKYCを完了
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* プログレスバー（duration > 0の場合のみ） */}
        {duration > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg overflow-hidden">
            <div
              className="h-full bg-gray-400 transition-all ease-linear"
              style={{
                animation: `shrink ${duration}ms linear`,
                transformOrigin: 'left',
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes shrink {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }
      `}</style>
    </div>
  );
};

/**
 * トーストコンテナーコンポーネント
 */
const ToastContainer = ({ toasts, onClose }) => {
  return (
    <div className="fixed top-4 right-4 z-[10000] space-y-2">
      {toasts.map((toast) => (
        <ToastMessage key={toast.id} {...toast} onClose={onClose} />
      ))}
    </div>
  );
};

/**
 * トーストコンテキスト
 */
const ToastContext = createContext();

/**
 * トーストプロバイダー
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      duration: 5000, // デフォルト5秒
      ...toast,
    };

    setToasts((prev) => [...prev, newToast]);
    return id;
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const clearAllToasts = () => {
    setToasts([]);
  };

  // 便利メソッド
  const showSuccess = (message, title = '成功', options = {}) => {
    return addToast({
      type: TOAST_TYPES.SUCCESS,
      title,
      message,
      ...options,
    });
  };

  const showError = (message, title = 'エラー', options = {}) => {
    return addToast({
      type: TOAST_TYPES.ERROR,
      title,
      message,
      duration: 8000, // エラーは長めに表示
      ...options,
    });
  };

  const showWarning = (message, title = '警告', options = {}) => {
    return addToast({
      type: TOAST_TYPES.WARNING,
      title,
      message,
      ...options,
    });
  };

  const showInfo = (message, title = '情報', options = {}) => {
    return addToast({
      type: TOAST_TYPES.INFO,
      title,
      message,
      ...options,
    });
  };

  const showMining = (
    amount,
    ecosystemCategory,
    tokenType = 'SHINKETSU',
    options = {}
  ) => {
    return addToast({
      type: TOAST_TYPES.MINING,
      title: 'マイニング成功！',
      message: `${ecosystemCategory || ''}での活動により${amount} ${tokenType}を獲得しました`,
      data: { amount, ecosystemCategory, tokenType },
      duration: 6000,
      ...options,
    });
  };

  const showOpportunityLoss = (
    amount,
    tokenType = 'SHINKETSU',
    onKycClick,
    options = {}
  ) => {
    return addToast({
      type: TOAST_TYPES.OPPORTUNITY_LOSS,
      title: '機会損失が発生',
      message: `本人確認完了で${amount} ${tokenType}獲得可能でした`,
      data: { amount, tokenType, onKycClick },
      duration: 10000, // 機会損失は長めに表示
      ...options,
    });
  };

  const value = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showMining,
    showOpportunityLoss,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * トーストフック
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * マイニング結果専用のフック
 */
export const useMiningToast = () => {
  const toast = useToast();

  const notifyMiningSuccess = (result) => {
    if (result.success) {
      toast.showMining(
        result.amount,
        result.ecosystem_category,
        result.token_type || 'SHINKETSU'
      );
    } else if (result.requires_kyc) {
      toast.showOpportunityLoss(
        result.calculated_amount || result.amount,
        result.token_type || 'SHINKETSU',
        () => {
          // KYCページへリダイレクト
          window.location.href = '/kyc';
        }
      );
    } else {
      toast.showError(
        result.reason || 'マイニングに失敗しました',
        'マイニングエラー'
      );
    }
  };

  return { notifyMiningSuccess };
};

export default ToastMessage;
